"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";

import { safeNext, useLogin } from "../../../_lib/hooks";
import { Tooltip } from "../../../_ui/Tooltip";
import { TextField } from "../shell/form";
import { Action, Card, PageTitle, Problem, Waiting } from "../shell/parts";
import { Shell } from "../shell/Shell";

/*
 * 로그인 — 시안 08 (`/login`).
 *
 * 🔴 **식별자는 이메일입니다**(오너 결정 2026-09-07 #3 · `POST /auth/login` 이 `email` 만 받습니다).
 *    시안의 «휴대폰 번호 / 아이디» 전환은 휴대폰 로그인이 없어 두지 않았습니다 — 동작하지 않는
 *    탭을 그리면 누른 사람이 막힙니다(`docs/base/11` §7-3). 휴대폰 로그인이 생기면 그 자리입니다.
 * 🔴 **«비밀번호 찾기» 도 없습니다** — 몰 비밀번호 재설정은 메일 경로가 정해지지 않았습니다
 *    (AGENTS.md §2-3 «아직 없는 것»).
 * 🔑 간편 로그인(카카오·네이버·구글)은 **자리만**(README §11-7) — 로고 없이, 누를 수 없습니다.
 * 🔑 `next` — 로그인 유도 시트(02)에서 왔으면 고른 상품권이 담긴 구매 화면으로 돌아갑니다.
 */
export default function Login() {
  return (
    <Shell top="loose">
      <Suspense fallback={<Card><Waiting /></Card>}>
        <LoginForm />
      </Suspense>
    </Shell>
  );
}

function LoginForm() {
  const params = useSearchParams();
  const next = safeNext(params.get("next"));
  const { login, busy, error, fields } = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    await login(email, password, next);
  }

  const registerHref = next === "/" ? "/register" : `/register?next=${encodeURIComponent(next)}`;

  return (
    <>
      <PageTitle>로그인</PageTitle>
      <Card>
        <form onSubmit={submit} noValidate>
          <div className="grid gap-3.5">
            {error && !fields.email && !fields.password && <Problem message={error} />}
            <TextField label="이메일" type="email" value={email} onChange={setEmail} placeholder="hong@example.com" autoComplete="email" inputMode="email" required error={fields.email || undefined} />
            <TextField label="비밀번호" type="password" value={password} onChange={setPassword} placeholder="••••••••" autoComplete="current-password" required error={fields.password || undefined} />
          </div>
          <div className="mt-4">
            <Action type="submit" size="xl" loading={busy} loadingText="로그인하는 중입니다" tooltip="로그인하고 하던 화면으로 돌아갑니다.">
              로그인
            </Action>
          </div>
        </form>
        <p className="mt-3.5 text-center text-body text-ink-faint">
          처음이신가요?{" "}
          <Link href={registerHref} className="font-bold text-ink underline-offset-4 hover:underline">회원 가입</Link>
        </p>
      </Card>

      <div className="mx-1 mt-[22px] mb-[18px] flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-caption text-ink-faint">간편 로그인</span>
        <span className="h-px flex-1 bg-line" />
      </div>
      <div className="flex justify-center gap-[26px]">
        {["카카오", "네이버", "구글"].map((name) => (
          <Tooltip key={name} text={`${name} 로그인은 준비 중입니다.`}>
            {(trigger) => (
              <button type="button" aria-disabled="true" className="flex cursor-not-allowed flex-col items-center gap-[7px]" {...trigger}>
                {/* 🔑 원은 `round` 를 따라가지 않습니다(README §4). 로고는 넣지 않습니다(README §11-7). */}
                <span aria-hidden="true" className="grid size-[54px] place-items-center rounded-full bg-raised text-[length:calc(var(--tnf-text-base)*11/14)] font-bold text-ink-faint shadow-[inset_0_0_0_1px_var(--tnf-line)]">
                  준비 중
                </span>
                <span className="text-[length:calc(var(--tnf-text-base)*12.5/14)] text-ink-soft">{name}</span>
              </button>
            )}
          </Tooltip>
        ))}
      </div>
    </>
  );
}
